<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';

// Per-company panel origins — the panel HTML is served by the company's own
// backend so Order/Product/Customer data comes from that company's database
// (mk-backend :3668/sapi vs mk-backend-s3 :3669/sapi3).
const STORE_DASHBOARD_URLS =
  window.location.hostname === 'localhost'
    ? {
        mk: 'http://localhost:3668/api/store-dashboard',
        s3: 'http://localhost:3669/api/store-dashboard',
      }
    : {
        mk: 'https://sapi.mkhb.my/api/store-dashboard',
        s3: 'https://sapi3.mkhb.my/api/store-dashboard',
      };

// Current Chatwoot color scheme — themeHelper.js toggles `dark` on <body>.
const getTheme = () =>
  document.body.classList.contains('dark') ? 'dark' : 'light';

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const getInboxById = useMapGetter('inboxes/getInbox');

// Company of the current conversation, by inbox-name suffix — same convention
// as ChatList's company views (-MK / -S3, owner rule 2026-07-01). Suffix-less
// inboxes (legacy WhatsApp, test inboxes) and no-conversation default to MK.
const panelCompany = computed(() => {
  const inbox = getInboxById.value(currentChat.value?.inbox_id);
  return /[-\s]S3$/i.test((inbox?.name || '').trim()) ? 's3' : 'mk';
});

// Seed the iframe with the initial scheme so the dashboard renders in the right
// theme with no flash; later theme toggles are pushed via postMessage (see
// themeObserver) without reloading. Switching between an MK and an S3
// conversation changes the src and DOES reload the iframe — intended, the two
// companies' panels are different documents.
const storeDashboardSrc = computed(
  () => `${STORE_DASHBOARD_URLS[panelCompany.value]}?theme=${getTheme()}`
);

const iframeRef = ref(null);
const hasLoadedOnce = ref(false);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeStorePanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_store_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
      is_store_panel_open: false,
    });
  }
};

// Explicit close (the dashboard's ✕ button posts `close-panel`). Works on every
// screen size — unlike closeStorePanel, which only auto-closes on mobile.
const closePanelFully = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_store_panel_open: false,
  });
};

const postContextToIframe = () => {
  if (!iframeRef.value?.contentWindow) return;
  const contact = currentChat.value?.meta?.sender || {};
  const eventData = {
    event: 'appContext',
    data: {
      conversation: currentChat.value,
      contact,
      currentAgent: {
        id: currentUser.value?.id,
        name: currentUser.value?.name,
        email: currentUser.value?.email,
      },
      theme: getTheme(),
    },
  };
  iframeRef.value.contentWindow.postMessage(JSON.stringify(eventData), '*');
};

const sendMessageToConversation = async message => {
  if (!currentChat.value?.id || !message) return;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: currentChat.value.id,
      message,
      private: false,
      sender: currentUser.value,
    });
    // Notify iframe of success
    iframeRef.value?.contentWindow?.postMessage(
      JSON.stringify({ event: 'messageSent', success: true }),
      '*'
    );
  } catch (error) {
    iframeRef.value?.contentWindow?.postMessage(
      JSON.stringify({
        event: 'messageSent',
        success: false,
        error: error.message,
      }),
      '*'
    );
  }
};

const onIframeLoad = () => {
  hasLoadedOnce.value = true;
  postContextToIframe();
};

const handleMessage = event => {
  if (event.data === 'chatwoot-dashboard-app:fetch-info') {
    postContextToIframe();
    return;
  }
  // Handle structured messages from iframe
  try {
    const data =
      typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (data.event === 'send-message') {
      sendMessageToConversation(data.message);
    } else if (data.event === 'jump-to-message' && data.messageId) {
      // Wire iframe → Chatwoot's MessagesView scroll logic.
      // Same DOM target as MessagesView's onScrollToMessage handler:
      // `document.getElementById('message' + messageId)`.
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: data.messageId });
    } else if (data.event === 'close-panel') {
      closePanelFully();
    }
  } catch {
    // Not JSON, ignore
  }
};

watch(
  () => currentChat.value?.id,
  () => {
    if (hasLoadedOnce.value) {
      postContextToIframe();
    }
  }
);

// Push fresh context to the iframe whenever a new message arrives in the
// SAME conversation — needed so "Mentioned in this chat" picks up newly
// pasted order IDs without forcing CS to F5. Watching messages.length is
// cheap (just an integer compare) and fires within ~50ms of Vuex update.
watch(
  () => currentChat.value?.messages?.length,
  (newLen, oldLen) => {
    if (hasLoadedOnce.value && newLen && newLen !== oldLen) {
      postContextToIframe();
    }
  }
);

// Push scheme changes to the iframe without reloading it — Chatwoot toggles the
// `dark` class on <body> when the user switches theme.
const pushThemeToIframe = () => {
  iframeRef.value?.contentWindow?.postMessage(
    JSON.stringify({ event: 'theme', theme: getTheme() }),
    '*'
  );
};

let themeObserver = null;

onMounted(() => {
  window.addEventListener('message', handleMessage);
  themeObserver = new MutationObserver(pushThemeToIframe);
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage);
  themeObserver?.disconnect();
});
</script>

<template>
  <div
    v-on-click-outside="() => closeStorePanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col md:flex fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <iframe
      ref="iframeRef"
      :src="storeDashboardSrc"
      class="w-full h-full border-0"
      allow="clipboard-write"
      @load="onIframeLoad"
    />
  </div>
</template>
